import { useGlobalStore } from '@/stores/global-store'
import { Consent, PatientConsentDocument, RequiredConsent, UserConsent } from '@/types'
import { useFetchWithInterceptor } from '@/use'
import { PatientGroupedConsents } from '@/types/patient-grouped-consents.model'
import { PatientAvailableConsent } from '@/types/patient-available-consent.model'

export const fetchUserRequiredConsents = async (language?: string): Promise<RequiredConsent[]> => {
  const globalStore = useGlobalStore()
  const { response, request } = useFetchWithInterceptor<RequiredConsent[]>(
    `${import.meta.env.VITE_API_URL}/account/required-consents?language=${language || globalStore.language || 'en'}`,
    {
      method: 'GET',
    },
  )
  await request()
  if (response.value) {
    return response.value.map((item) => new RequiredConsent(JSON.parse(JSON.stringify(item))))
  } else {
    throw new Error('Failed to fetch user permissions.')
  }
}

export const fetchNLHConsents = async (nlhId: number): Promise<Consent[]> => {
  const { response, request } = useFetchWithInterceptor<Consent[]>(
    `${import.meta.env.VITE_API_URL}/nlhs/${nlhId}/consents`,
    {
      method: 'GET',
    },
  )
  await request()
  if (response.value) {
    return response.value.map((item) => new Consent(JSON.parse(JSON.stringify(item))))
  } else {
    throw new Error('Failed to fetch user permissions.')
  }
}

export const fetchCoachConsents = async (coachId: number): Promise<Consent[]> => {
  const { response, request } = useFetchWithInterceptor<Consent[]>(
    `${import.meta.env.VITE_API_URL}/coaches/${coachId}/consents`,
    {
      method: 'GET',
    },
  )
  await request()
  if (response.value) {
    return response.value.map((item) => new Consent(JSON.parse(JSON.stringify(item))))
  } else {
    throw new Error('Failed to fetch user permissions.')
  }
}

export const loadDocument = async (url: string): Promise<string> => {
  const response = await fetch(url)
  return await response.text()
}

export const fetchPatientConsents = async (patientId: number): Promise<UserConsent[]> => {
  const { response, request } = useFetchWithInterceptor<UserConsent[]>(
    `${import.meta.env.VITE_API_URL}/patients/${patientId}/consents`,
    {
      method: 'GET',
    },
  )
  await request()
  if (response.value) {
    return response.value.map((item) => new UserConsent(JSON.parse(JSON.stringify(item))))
  } else {
    throw new Error('Failed to fetch user permissions.')
  }
}

export const fetchPatientGroupedConsents = async (patientId: number): Promise<PatientGroupedConsents> => {
  const { response, request } = useFetchWithInterceptor<PatientGroupedConsents>(
    `${import.meta.env.VITE_API_URL}/patients/${patientId}/consents/grouped`,
    {
      method: 'GET',
    },
  )
  await request()
  if (response.value) {
    return new PatientGroupedConsents(response.value)
  } else {
    throw new Error('Failed to fetch user permissions.')
  }
}

export const fetchPatientConsentDocuments = async (patientId: number): Promise<PatientConsentDocument[]> => {
  const { response, request } = useFetchWithInterceptor<PatientConsentDocument[]>(
    `${import.meta.env.VITE_API_URL}/patients/${patientId}/consents/documents`,
    {
      method: 'GET',
    },
  )
  await request()
  if (response.value) {
    return response.value.map((item) => new PatientConsentDocument(JSON.parse(JSON.stringify(item))))
  } else {
    throw new Error('Response is undefined.')
  }
}

export const fetchPatientAvailableConsents = async (language?: string): Promise<PatientAvailableConsent[]> => {
  const { response, request } = useFetchWithInterceptor<PatientAvailableConsent[]>(
    `${import.meta.env.VITE_API_URL}/patients/available-consents${language ? `?language=${language}` : ''}`,
    {
      method: 'GET',
    },
  )
  await request()
  if (response.value) {
    return response.value.map((item) => new PatientAvailableConsent(JSON.parse(JSON.stringify(item))))
  } else {
    throw new Error('Response is undefined.')
  }
}
